import { Actions } from '@ant-design/x';
import { App } from 'antd';
import { Copy, RotateCcw, Square, Volume2 } from 'lucide-react';
import { useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { getSpeakingText, subscribeSpeaking, toggleSpeakText } from '../../lib/chat/messageActions';

type Props = {
  content: string;
  disabled?: boolean;
  onRetry?: () => void;
};

const iconProps = { className: 'h-3.5 w-3.5', strokeWidth: 1 as const, 'aria-hidden': true };

/** Assistant bubble footer — Copy / Regenerate / Speak (AIGrant ChatMessageActions). */
export function DocumentChatMessageActions({ content, disabled, onRetry }: Props) {
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const text = content.trim();
  const speakingText = useSyncExternalStore(subscribeSpeaking, getSpeakingText, () => null);
  if (!text) return null;

  const isSpeakingThis = speakingText === text;

  const items = [
    {
      key: 'copy',
      label: t('common.copy'),
      icon: <Copy {...iconProps} />,
    },
    ...(onRetry
      ? [
          {
            key: 'retry',
            label: t('chat.regenerate'),
            icon: <RotateCcw {...iconProps} />,
          },
        ]
      : []),
    {
      key: 'speak',
      label: isSpeakingThis ? t('chat.stopReading') : t('chat.readAloud'),
      icon: isSpeakingThis ? <Square {...iconProps} /> : <Volume2 {...iconProps} />,
    },
  ];

  return (
    <Actions
      variant="borderless"
      items={items}
      onClick={({ key }) => {
        if (disabled) return;
        if (key === 'copy') {
          void navigator.clipboard.writeText(text).catch(() => {
            message.error(t('chat.genericError'));
          });
          return;
        }
        if (key === 'retry') {
          onRetry?.();
          return;
        }
        if (key === 'speak') {
          try {
            toggleSpeakText(text, i18n.language || 'en-US');
          } catch {
            message.error(t('chat.speechUnavailable'));
          }
        }
      }}
    />
  );
}

export default DocumentChatMessageActions;
